import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { Gear } from '../shared/gear.model';
import { GearSelectDialogComponent } from '../gear-select-dialog/gear-select-dialog.component';
import { GearEditDialogComponent } from '../gear-edit-dialog/gear-edit-dialog.component';
import { ConfirmDialogComponent } from '../vessel-reg/confirm-dialog/confirm-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { SwUpdate } from '@angular/service-worker';
import { Subscription } from 'rxjs';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { AddFisheryDialogComponent } from '../add-fishery-dialog/add-fishery-dialog.component';
import { LookupItem } from '../shared/lookup-item.model';

let SCREEN_WIDTH: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
let SCREEN_HEIGHT: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

/**
 * Settings page. Here we see the vessels, favorite species, gears.
 * Check for updates and allow manual sync here.
 */
@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {

  // current user
  user: OperatorUser = LocalStorageService.current_user;
  //Restricted to groundfish for mow
  favSpeciesList: LookupItem[] = LocalStorageService.current_user.favoriteSpecies;
  userObjSubscription!: Subscription;
  syncDate: string = '';
  syncDateSub!: Subscription;

  userGear: Gear[] = [];
  gearSubscrption!: Subscription;

  syncInProgress: boolean = LocalStorageService.sync_in_progress;
  syncProgSub!: Subscription;

  constructor(
    public router: Router,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private onlineStatus: OnlineStatusService,
    protected common: CommonFunctionService,
    private update: SwUpdate) {

  }

  /**
   * Get the current user and their gear. Set the sync date.
   */
  ngOnInit(): void {
    LocalStorageService.sync_in_progress$.subscribe(val => this.syncInProgress = val);
    LocalStorageService.storage_promise.then(_ => {
      this.user = LocalStorageService.current_user;
      this.userGear = LocalStorageService.user_gear;
      console.log(this.userGear)
      this.gearSubscrption = LocalStorageService.user_gear$.subscribe(val => this.userGear = val);
      this.userObjSubscription = LocalStorageService.current_user$.subscribe(val => this.user = val);
      this.syncDateSub = LocalStorageService.last_sync$.subscribe(val => {console.log(val); this.formatSyncDate();});
    });
  }


  formatSyncDate() {
    let syncDate = new Date();
    let lastSyncTime = new Date(LocalStorageService.last_sync);
    if (lastSyncTime.getDay() == syncDate.getDay() && 
        lastSyncTime.getMonth() == syncDate.getMonth() && 
        lastSyncTime.getFullYear() == syncDate.getFullYear()) {
          this.syncDate = formatDate(lastSyncTime, 'mediumTime', 'en-US');
    } else this.syncDate = formatDate(lastSyncTime, 'medium', 'en-US'); 
  }


  /**
   * "Check for Update" button selected. Same procedure run in app.component constructor.
   */ 
  checkForUpdate(): void {

    console.log('Checking for update...');

    if (this.onlineStatus.getStatus() === OnlineStatusType.OFFLINE) {
      this.toastr.warning('Internet connection is required to check for updates.', 'Error');
      return;
    }

    // status online, check or update
    this.toastr.warning('', 'Checking for update(s)...');
    this.update.checkForUpdate().then((update: boolean) => {

      // new app version found, updating on reload
      if (update) {
        this.toastr.success('New version found, reloading...', 'Update ready');
        document.location.reload();
      }

      else {
        this.toastr.success('You are using the latest version', 'App on latest version');
      }
    }).catch(() => {
      this.toastr.error('Cannot check for updates at this time', 'Error');
    });
  }

  // My Vessels

  /**
   * Add a vessel in Settings
   */ 
  addVessel(): void {
    this.common.addVessel(-1, false).catch((err: any) => console.log(err));
  }


  /**
   * Edit an existing vessel in Settings
   */ 
  editVessel(index: number): void {
    this.common.addVessel(index, true).catch((err: any) => console.log(err));

  }


  /**
   * Add new favorite species in Settings
   */ 
  addSpecies(): void {
    let fishery: "Combined" | "Groundfish" | "HMS" | "Trawl" = this.user.hmsFishery && this.user.groundfishFishery ? "Combined" : 
    this.user.hmsFishery ? "HMS" : this.user.groundfishFishery ? "Groundfish" : "Trawl";
    this.common.addSpecies(-1, fishery).then(() => this.favSpeciesList = LocalStorageService.current_user.favoriteSpecies);
  }

  /**
   * Editing an existing
   * @param index Index into user's favorite species array
   */
  editSpecies(index: number): void {
    this.common.addSpecies(index, "Combined").then(() => this.favSpeciesList = LocalStorageService.current_user.favoriteSpecies);
  }


  /**
 * 
 */
  addRemoveFishery(): void {
    const dialogRef = this.dialog.open(AddFisheryDialogComponent);
    dialogRef.afterClosed().subscribe(result => {
      console.log(`Fishery selected: ${result}`);
    });
  }


  /**
   * Add a new gear as in set-detail component
   */
  addGear(): void {

    // dialog dimensions
    let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
    let h: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

    const dialogRef: MatDialogRef<GearSelectDialogComponent, any> = this.dialog.open(GearSelectDialogComponent, {
      width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
      height: SCREEN_HEIGHT > h ? SCREEN_HEIGHT : h,
      data: { screen: "Settings", gearIndex: -1, setType: '' }
    });


    dialogRef.afterClosed().subscribe((result: any) => {

      if (result >= 0) {
        localStorage.setItem('last_gear', result.gearID);
      }

      // update gear list in settings
      this.userGear = LocalStorageService.user_gear;

    });
  }

  /**
   * Edit an existing gear
   * @param index Index into user gear array
   */
  editGear(index: number): void {

    let currGear: Gear = this.userGear[index];

    const dialogRef: MatDialogRef<GearEditDialogComponent, any> = this.dialog.open(GearEditDialogComponent, {
      autoFocus: false,
      width: '425px',
      data: {index: index, isHMS: currGear.fishery == 'HMS'}
    });

    // update gear list in settings
    dialogRef.afterClosed().subscribe((result: any) => {

      if (result) {

        // edit gear
        if (result == 'edit') {


          // dialog dimensions
          let w: string = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
          let h: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

          const dialogRef: MatDialogRef<GearSelectDialogComponent, any> = this.dialog.open(GearSelectDialogComponent, {
            width: SCREEN_WIDTH > w ? SCREEN_WIDTH : w,
            height: SCREEN_HEIGHT > h ? SCREEN_HEIGHT : h,
            data: { screen: "Settings", gearIndex: index }
          });

          dialogRef.afterClosed().subscribe((result: any) => {

            if (result >= 0) {
              localStorage.setItem('last_gear', result.gearID);
            }

            // update gear list in settings
            this.userGear = LocalStorageService.user_gear;
          });
        }

        // delete gear
        else if (result == 'delete') {
          let id = currGear.gearID;

          if (LocalStorageService.user_trip.sets.some(s => s.gear.gearID == id)) {
            console.log('Cannot delete');
            this.toastr.error('Gear is currently attached to one or more set(s), cannot delete.', '', { timeOut: 5000, extendedTimeOut: 5000 });
            return;
          } else {
            const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
              autoFocus: false,
              data: { screen: 'deletegear' },
            });
  
            // close confirm dialog, delete gear
            dialogRef.afterClosed().subscribe((result: boolean) => {
              if (result) {
                LocalStorageService.deleteGear(id).then((res: boolean) => {
                  if (res) {
                    LocalStorageService.user_gear = LocalStorageService.user_gear.filter(g => g.gearID != id);
                    this.userGear = LocalStorageService.user_gear;
                    this.toastr.success("Successfully deleted gear " + currGear.identifier, "Success!");
                  } else {
                    this.toastr.error("Could not delete gear " + currGear.identifier, "Failure");
                  }
                });
              }
  
              // update gear list in settings
              this.userGear = LocalStorageService.user_gear;
            });
          }         
        }
      }
    });
  }


  /**
   * Sync data, that is just reload since sync happens automatically then.
   */
  performDataSync(): void {
    if (this.onlineStatus.getStatus() == OnlineStatusType.OFFLINE) this.toastr.error('Could not sync, check connection', 'Sync failed');
    else {
      this.toastr.success('Reloading to sync..', 'Initiating sync');
      document.location.reload();
    }
  }


  ngOnDestroy(): void {
    if (this.gearSubscrption) this.gearSubscrption.unsubscribe();
    if (this.userObjSubscription) this.userObjSubscription.unsubscribe();
    if (this.syncDateSub) this.syncDateSub.unsubscribe();
    if (this.syncProgSub) this.syncProgSub.unsubscribe();
  }

}
